import { useState, useEffect } from 'react'
import { Navigate, useNavigate } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import { entitlementsOf } from '../../utils/entitlements'
import { trainingBlockApi } from '../../api'
import type { ParsedTrainingBlock, TrainingTagsGroupedDto } from '../../api/trainingBlockApi'
import { BLOCK_TAG_CATEGORIES } from '../../api/trainingBlockApi'
import { BlockItemsEditor } from './BlockItemsEditor'
import { emptyItem, itemsFromParsed, itemsToApi, type ItemRow } from './blockItemHelpers'
import { TagSelector } from './TagSelector'
import type { ApiError } from '../../api/client'
import { Button, Badge } from '../ui'
import { useTranslation } from '../../i18n/useTranslation'
import { useTheme } from '../../hooks/useTheme'
import { getFormErrorStyle, getInputStyle, INPUT_CLASS_NAME, TEXTAREA_CLASS_NAME } from '../../utils/themeStyles'
import {
  AI_PARSE_BLOCK_MAX_CHARS,
  AI_PARSE_MIN_CHARS,
  AI_PARSE_REPARSE_COMMENT_MAX_CHARS,
  mapParseApiErrorMessage,
  parseTextValidationMessage,
  reparseCommentValidationMessage,
  validateParseRawText,
  validateReparseComment,
} from '../../utils/aiParseValidation'

const MAX_CORRECTIONS = 3

export default function CreateTrainingBlockFromText() {
  const navigate = useNavigate()
  const theme = useTheme()
  const { t, translate } = useTranslation()
  const { user } = useAuth()
  const blocked = !entitlementsOf(user).createOwnBlocksAndPrograms

  const [rawText, setRawText] = useState('')
  const [parsed, setParsed] = useState<ParsedTrainingBlock | null>(null)
  const [blockName, setBlockName] = useState('')
  const [selectedTags, setSelectedTags] = useState<string[]>([])
  const [items, setItems] = useState<ItemRow[]>([emptyItem()])
  const [availableTags, setAvailableTags] = useState<TrainingTagsGroupedDto>({
    muscles: [], levels: [], genders: [], types: [],
  })
  const [correction, setCorrection] = useState('')
  const [correctionsUsed, setCorrectionsUsed] = useState(0)
  const [sessionId, setSessionId] = useState<string | null>(null)
  const [parsing, setParsing] = useState(false)
  const [reparsing, setReparsing] = useState(false)
  const [confirming, setConfirming] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    trainingBlockApi.getTags().then(setAvailableTags).catch(() => {})
  }, [])

  if (blocked) {
    return <Navigate to="/training-blocks" replace />
  }

  function applyParsed(result: ParsedTrainingBlock) {
    setParsed(result)
    setBlockName(result.name)
    setSelectedTags(result.tags ?? [])
    setItems(itemsFromParsed(result.items))
  }

  function currentParsedBlock(): ParsedTrainingBlock {
    return {
      name: blockName.trim() || parsed?.name || '',
      tags: selectedTags,
      items: itemsToApi(items).map(item => ({
        note: item.note ?? null,
        exercises: item.exercises,
      })),
    }
  }

  const rawTextValidation = validateParseRawText(rawText, AI_PARSE_BLOCK_MAX_CHARS)
  const correctionValidation = validateReparseComment(correction)
  const canParse = !rawTextValidation && !parsing
  const canReparse = !correctionValidation && !reparsing && !!correction.trim()

  async function handleParse() {
    const validationError = validateParseRawText(rawText, AI_PARSE_BLOCK_MAX_CHARS)
    if (validationError) {
      setError(parseTextValidationMessage(validationError, translate, AI_PARSE_BLOCK_MAX_CHARS))
      return
    }
    setError(null)
    setParsing(true)
    try {
      const response = await trainingBlockApi.parseBlock(rawText.trim())
      setSessionId(response.sessionId)
      applyParsed(response.result)
      setCorrectionsUsed(0)
      setCorrection('')
    } catch (err) {
      const apiError = err as ApiError
      if (apiError.status === 429) {
        setError(apiError.message || t.createTrainingBlockFromText.error.quotaExceeded)
      } else {
        setError(mapParseApiErrorMessage(
          apiError.message,
          translate,
          AI_PARSE_BLOCK_MAX_CHARS,
          t.createTrainingBlockFromText.error.parseFailed,
        ))
      }
    } finally {
      setParsing(false)
    }
  }

  async function handleReparse() {
    if (!parsed || !sessionId) return
    const validationError = validateReparseComment(correction)
    if (validationError) {
      setError(reparseCommentValidationMessage(validationError, translate))
      return
    }
    setError(null)
    setReparsing(true)
    try {
      const response = await trainingBlockApi.reparseBlock(sessionId, currentParsedBlock(), correction.trim())
      setSessionId(response.sessionId)
      applyParsed(response.result)
      setCorrectionsUsed(prev => prev + 1)
      setCorrection('')
    } catch (err) {
      const apiError = err as ApiError
      if (apiError.status === 429) {
        setError(apiError.message || t.createTrainingBlockFromText.error.quotaExceeded)
      } else {
        setError(mapParseApiErrorMessage(
          apiError.message,
          translate,
          AI_PARSE_BLOCK_MAX_CHARS,
          t.createTrainingBlockFromText.error.parseFailed,
        ))
      }
    } finally {
      setReparsing(false)
    }
  }

  async function handleConfirm() {
    if (!parsed) return
    setError(null)
    const payloadItems = itemsToApi(items)
    if (payloadItems.length === 0) {
      setError(t.createTrainingBlock.error.noExercises)
      return
    }
    setConfirming(true)
    try {
      await trainingBlockApi.createBlock({
        name: blockName.trim() || parsed.name,
        tags: selectedTags,
        items: payloadItems,
      })
      navigate('/training-blocks')
    } catch (err) {
      const apiError = err as ApiError
      setError(apiError.message || t.createTrainingBlockFromText.error.createFailed)
    } finally {
      setConfirming(false)
    }
  }

  function handleStartOver() {
    setParsed(null)
    setBlockName('')
    setItems([emptyItem()])
    setCorrection('')
    setCorrectionsUsed(0)
    setSessionId(null)
    setError(null)
  }

  const correctionsLeft = MAX_CORRECTIONS - correctionsUsed
  const canCorrect = correctionsLeft > 0

  return (
    <div className="p-5 pb-24">
      <header className="mb-6">
        <div className="flex items-center gap-2 flex-wrap mb-1">
          <h1 className="text-2xl font-bold" style={{ color: theme.text.primary }}>
            {t.createTrainingBlockFromText.title}
          </h1>
          <Badge variant="warning" size="sm">Experimental</Badge>
        </div>
        <p className="text-sm" style={{ color: theme.text.muted }}>
          {t.createTrainingBlockFromText.subtitle}
        </p>
      </header>

      {!parsed ? (
        <div className="flex flex-col gap-5">
          <div>
            <label className="block text-sm font-medium mb-2" style={{ color: theme.text.primary }}>
              {t.createTrainingBlockFromText.textareaLabel}
            </label>
            <textarea
              value={rawText}
              onChange={e => {
                setRawText(e.target.value)
                setError(null)
              }}
              placeholder={t.createTrainingBlockFromText.textareaPlaceholder}
              rows={8}
              maxLength={AI_PARSE_BLOCK_MAX_CHARS}
              className={TEXTAREA_CLASS_NAME}
              style={getInputStyle(theme)}
            />
            <p className="text-xs mt-1" style={{ color: theme.text.muted }}>
              {translate('aiParse.validation.hint', {
                min: AI_PARSE_MIN_CHARS,
                max: AI_PARSE_BLOCK_MAX_CHARS,
              })}
            </p>
            {rawText.length > 0 && rawTextValidation && (
              <p className="text-xs mt-1" style={{ color: '#ef4444' }}>
                {parseTextValidationMessage(rawTextValidation, translate, AI_PARSE_BLOCK_MAX_CHARS)}
              </p>
            )}
          </div>
          {error && (
            <div className="rounded-xl p-4" style={getFormErrorStyle()}>
              <p className="text-sm" style={{ color: theme.text.primary }}>{error}</p>
            </div>
          )}
          <Button type="button" variant="primary" size="lg" fullWidth disabled={!canParse} onClick={handleParse}>
            {parsing ? t.createTrainingBlockFromText.parsing : t.createTrainingBlockFromText.parseButton}
          </Button>
        </div>
      ) : (
        <div className="flex flex-col gap-5">
          <div>
            <label className="block text-sm font-medium mb-2" style={{ color: theme.text.primary }}>
              {t.createTrainingBlock.nameLabel}
            </label>
            <input
              type="text"
              value={blockName}
              onChange={e => setBlockName(e.target.value)}
              className={INPUT_CLASS_NAME}
              style={getInputStyle(theme)}
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2" style={{ color: theme.text.primary }}>
              {t.createTrainingBlock.tagsLabel}
            </label>
            <TagSelector
              catalog={availableTags}
              categories={BLOCK_TAG_CATEGORIES}
              selected={selectedTags}
              onChange={setSelectedTags}
            />
          </div>
          <BlockItemsEditor
            items={items}
            onChange={setItems}
            textColor={theme.text.primary}
            bgColor={theme.bgColor}
            labels={{
              itemsLabel: t.createTrainingBlock.exercisesLabel,
              addExercise: t.createTrainingBlock.addExercise,
              addToGroup: t.createTrainingBlock.addToGroup,
              notePlaceholder: t.createTrainingBlock.notePlaceholder,
              addNote: t.createTrainingBlock.addNote,
              changeNote: t.createTrainingBlock.changeNote,
              doneNote: t.createTrainingBlock.doneNote,
              exerciseNamePlaceholder: t.createTrainingBlock.exerciseNamePlaceholder,
              groupLabel: t.createTrainingBlock.groupLabel,
            }}
          />
          {canCorrect && (
            <div>
              <label className="block text-sm font-medium mb-2" style={{ color: theme.text.primary }}>
                {t.createTrainingBlockFromText.correctionsLabel}
              </label>
              <textarea
                value={correction}
                onChange={e => {
                  setCorrection(e.target.value)
                  setError(null)
                }}
                placeholder={t.createTrainingBlockFromText.correctionPlaceholder}
                rows={3}
                maxLength={AI_PARSE_REPARSE_COMMENT_MAX_CHARS}
                className={TEXTAREA_CLASS_NAME}
                style={getInputStyle(theme)}
              />
              {correction.length > 0 && correctionValidation && (
                <p className="text-xs mt-1" style={{ color: '#ef4444' }}>
                  {reparseCommentValidationMessage(correctionValidation, translate)}
                </p>
              )}
              <Button
                type="button"
                variant="secondary"
                className="mt-2"
                fullWidth
                disabled={!canReparse}
                onClick={handleReparse}
              >
                {reparsing ? t.createTrainingBlockFromText.reparsing : t.createTrainingBlockFromText.reparseButton}
              </Button>
              <p className="text-xs mt-1" style={{ color: theme.text.muted }}>
                {t.createTrainingBlockFromText.attemptsLeft.replace('{{count}}', String(correctionsLeft))}
              </p>
            </div>
          )}
          {error && (
            <div className="rounded-xl p-4" style={getFormErrorStyle()}>
              <p className="text-sm" style={{ color: theme.text.primary }}>{error}</p>
            </div>
          )}
          <Button type="button" variant="primary" size="lg" fullWidth disabled={confirming} onClick={handleConfirm}>
            {confirming ? t.createTrainingBlockFromText.confirming : t.createTrainingBlockFromText.confirmButton}
          </Button>
          <button
            type="button"
            onClick={handleStartOver}
            className="text-sm text-center active:opacity-75 transition-opacity underline"
            style={{ color: theme.text.muted }}
          >
            {t.createTrainingBlockFromText.startOver}
          </button>
        </div>
      )}
    </div>
  )
}
